import { el } from './ui'

// Every plain dropdown opens Roundly's own list on a computer (Liam, 8 Oct 2026: "some drop downs don't have our
// branding ... must have a good reason not to share styling"). The <select> itself stays exactly where it is, with
// its own size, value, labels and change handlers; only the list that opens is ours, in the same look as the
// Messages, Owed and Diary dropdowns (.roundly-select-list). One good reason to keep the system list: phones and
// tablets, where the thumb-sized system picker is the better control. Multi-select lists, list boxes (size > 1)
// and any select marked data-native="true" also keep their own.

let current: { select: HTMLSelectElement; list: HTMLElement; close: (refocus: boolean) => void; key: (event: KeyboardEvent) => void } | null = null
let installed = false
let serial = 0

const eligible = (s: unknown): s is HTMLSelectElement =>
  typeof HTMLSelectElement !== 'undefined' && s instanceof HTMLSelectElement && !s.multiple && s.size <= 1 && !s.disabled && !s.hidden && s.dataset.native !== 'true'
const onAComputer = (): boolean => typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover) and (pointer: fine)').matches

function nameOf(select: HTMLSelectElement): string {
  return select.getAttribute('aria-label') ?? select.labels?.[0]?.textContent?.trim() ?? 'Choose an option'
}

// Focus never leaves the <select> while the list is open (the list follows it with aria-activedescendant), so
// editors that save or close on blur, like the spreadsheet cells, see one normal change and nothing else.
function show(select: HTMLSelectElement): void {
  current?.close(false)
  const id = `roundly-pick-${++serial}`
  const list = el('div', { class: 'roundly-select-list', role: 'listbox', id, 'aria-label': nameOf(select) })
  const buttons: HTMLButtonElement[] = []
  const options: HTMLOptionElement[] = []
  let active = -1
  const choose = (option: HTMLOptionElement): void => {
    const changed = select.selectedIndex !== option.index
    select.selectedIndex = option.index
    close(true)
    if (changed) {
      select.dispatchEvent(new Event('input', { bubbles: true }))
      select.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }
  const add = (option: HTMLOptionElement): void => {
    if (option.hidden) return
    const button = el('button', { type: 'button', role: 'option', tabindex: '-1', id: `${id}-${buttons.length}`, 'aria-selected': String(option.selected) }, option.textContent ?? '', el('span', { class: 'roundly-select-check', 'aria-hidden': 'true' }, ' ✓'))
    button.disabled = option.disabled
    button.addEventListener('mousedown', (event) => event.preventDefault())
    button.addEventListener('click', () => { if (!option.disabled) choose(option) })
    buttons.push(button); options.push(option); list.append(button)
  }
  for (const node of Array.from(select.children)) {
    if (node instanceof HTMLOptGroupElement) {
      list.append(el('div', { class: 'roundly-select-group', role: 'presentation' }, node.label))
      for (const option of Array.from(node.children)) if (option instanceof HTMLOptionElement) add(option)
    } else if (node instanceof HTMLOptionElement) add(node)
  }
  if (!buttons.length) return
  const live = (): number[] => buttons.map((b, i) => b.disabled ? -1 : i).filter(i => i >= 0)
  const activate = (i: number): void => {
    buttons[active]?.classList.remove('is-active')
    active = i
    const b = buttons[i]
    if (!b) return
    b.classList.add('is-active')
    select.setAttribute('aria-activedescendant', b.id)
    b.scrollIntoView({ block: 'nearest' })
  }
  const step = (by: number | 'first' | 'last'): void => {
    const ids = live(); if (!ids.length) return
    const at = ids.indexOf(active)
    activate(by === 'first' ? ids[0]! : by === 'last' ? ids[ids.length - 1]! : ids[Math.min(ids.length - 1, Math.max(0, (at < 0 ? 0 : at + by)))]!)
  }
  let typed = '', typedAt = 0
  const key = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true) }
    else if (event.key === 'Tab') close(false)
    else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); event.stopPropagation(); step(event.key === 'ArrowDown' ? 1 : -1) }
    else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); event.stopPropagation(); step(event.key === 'Home' ? 'first' : 'last') }
    else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); const o = options[active]; if (o && !o.disabled) choose(o); else close(true) }
    else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
      event.preventDefault(); event.stopPropagation()
      typed = Date.now() - typedAt > 700 ? event.key : typed + event.key; typedAt = Date.now()
      const match = live().find(i => (buttons[i]!.firstChild?.textContent ?? '').toLowerCase().startsWith(typed.toLowerCase()))
      if (match !== undefined) activate(match)
    }
  }
  // Inside an open dialog the list must live in the dialog too, or it would sit behind it.
  const home = select.closest('dialog[open]') ?? document.body
  const rect = select.getBoundingClientRect()
  const vv = window.visualViewport
  const leftEdge = (vv?.offsetLeft ?? 0) + 8, topEdge = (vv?.offsetTop ?? 0) + 8
  const rightEdge = leftEdge + (vv?.width ?? window.innerWidth) - 16, bottomEdge = topEdge + (vv?.height ?? window.innerHeight) - 16
  const width = Math.min(Math.max(rect.width, 180), rightEdge - leftEdge)
  const below = bottomEdge - rect.bottom - 6, above = rect.top - topEdge - 6
  const upwards = below < 180 && above > below
  list.dataset.upwards = String(upwards)
  list.style.width = `${width}px`
  list.style.left = `${Math.max(leftEdge, Math.min(rect.left, rightEdge - width))}px`
  list.style.maxHeight = `${Math.max(44, Math.min(320, upwards ? above : below))}px`
  home.append(list)
  list.style.top = `${upwards ? Math.max(topEdge, rect.top - 6 - list.getBoundingClientRect().height) : rect.bottom + 6}px`
  select.setAttribute('aria-expanded', 'true'); select.setAttribute('aria-controls', id)
  const outside = (event: Event): void => { if (!list.contains(event.target as Node) && event.target !== select) close(false) }
  const scrolled = (event: Event): void => { if (!list.contains(event.target as Node)) close(false) }
  const resized = (): void => close(false)
  const gone = (): void => close(false)
  function close(refocus: boolean): void {
    if (current?.list !== list) return
    current = null
    list.remove()
    select.removeAttribute('aria-expanded'); select.removeAttribute('aria-activedescendant'); select.removeAttribute('aria-controls')
    document.removeEventListener('pointerdown', outside, true)
    document.removeEventListener('scroll', scrolled, true)
    window.removeEventListener('resize', resized)
    select.removeEventListener('blur', gone)
    if (refocus && select.isConnected && document.activeElement !== select) select.focus({ preventScroll: true })
  }
  current = { select, list, close, key }
  document.addEventListener('pointerdown', outside, true)
  document.addEventListener('scroll', scrolled, true)
  window.addEventListener('resize', resized)
  select.addEventListener('blur', gone)
  const chosen = buttons.findIndex(b => b.getAttribute('aria-selected') === 'true')
  if (chosen >= 0 && !buttons[chosen]!.disabled) activate(chosen); else step('first')
}

export function installBrandedSelects(): void {
  if (installed || typeof document === 'undefined' || typeof window === 'undefined') return
  installed = true
  document.addEventListener('mousedown', (event) => {
    if (event.button !== 0 || !onAComputer()) return
    const select = (event.target as Element | null)?.closest?.('select')
    if (!eligible(select)) return
    event.preventDefault()
    if (document.activeElement !== select) select.focus({ preventScroll: true })
    if (current?.select === select) current.close(false)
    else show(select)
  }, true)
  document.addEventListener('keydown', (event) => {
    if (current && event.target === current.select) { current.key(event); return }
    const select = event.target
    if (!eligible(select) || !onAComputer() || event.ctrlKey || event.metaKey || event.altKey && event.key !== 'ArrowDown') return
    // Spreadsheet cells keep Enter for saving the cell; the arrows and space still open the list.
    const opens = event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' && !select.classList.contains('cell-input')
    if (!opens) return
    event.preventDefault(); event.stopPropagation()
    show(select)
  }, true)
}
